import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminEarningsInfoSetupFieldsPanel } from "./Panels/AdminEarningsInfoSetupFieldsPanel";
import { AdminEarningsService } from "../../services/AdminEarningsService";
import { AdminEarningsSetupToolbar } from "./AdminEarningsSetupToolbar";
import { AdminEarningsSetupValidationSchema } from "./validator";
import { DDLDataTable } from "../DDLDataTable";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAdminEarnings } from "../../models/AdminEarnings";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { scrollPanelFieldsToTop, useNavigateToLogout } from "../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";

// import { SingleColumnLayout } from "../EmployeeAssignemnt/SingleColumnLayout";






export interface IAdminEarningsSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "adminEarnings-single-data-record";
const ADMIN_EARNINGS_LIST_DATA_QUERY_KEY = "adminEarnings-list";

const useGetData = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: () => {
			return AdminEarningsService.getById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

export const AdminEarningsSetup: React.FC<IAdminEarningsSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [adminEarningsId, setAdminEarningsId] = useState<number | string>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "adminEarningName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "adminEarningName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const { isPending: isSubmitting, mutate: submitAdminEarningsFormFields } =
		useMutation({
			mutationFn: AdminEarningsService.create,
			onSuccess: (res: IAdminEarnings | IServerError | IResponse | any) => {
				if (res.status === 200) {
					// if (instanceOfIAdminEarningsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					scrollPanelFieldsToTop();
					let path = `/payroll-earning/setup/${res.data.id}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [ADMIN_EARNINGS_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {
					// if (instanceOfIAdminEarningsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					scrollPanelFieldsToTop();
					let path = `/payroll-earning/setup/${res.data.id}`;
					navigate(path);
					setEditRecordId(res.data.id);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [ADMIN_EARNINGS_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		editRecordId,
		navigateToLogout
	);

	const DEFAULT_VALUES = {
		label: undefined,
		category: 200,
		t4Code: undefined,
		excludeFrom14: false,
		rl1Code: undefined,
		excludeFromA: false,
		type: 100,
		taxEngine: 200,

	};

	const formMethods = useForm<any>({
		resolver: yupResolver(AdminEarningsSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES,
	});

	const onSubmit = (values: any) => {
		let payload = {
			id: id && id,
			navigateToLogout: navigateToLogout,
			label: values.label,
			category: values.category,
			t4Code: values.t4Code,
			excludeFrom14: values.excludeFrom14,
			rl1Code: values.rl1Code,
			excludeFromA: values.excludeFromA,
			type: values.type,
			// showIn: values.showIn,
			taxEngine: values.taxEngine,
		};
		submitAdminEarningsFormFields(payload);
	};

	useEffect(() => {
		if (id && selectedDataRecord) {

			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteAdminEarningsById } =
		useMutation({
			mutationFn: AdminEarningsService.delAdminEarnings,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the AdminEarnings fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				scrollPanelFieldsToTop();
				let path = `/payroll-earning/setup`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [ADMIN_EARNINGS_LIST_DATA_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deleteAdminEarningsField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				adminEarningsId: adminEarningsId,
				navigateToLogout: navigateToLogout,
			};
			deleteAdminEarningsById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setAdminEarningsId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setAdminEarningsId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveAdminEarningsById } =
		useMutation({
			mutationFn: AdminEarningsService.archiveAdminEarnings,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the AdminEarnings fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/payroll-earning/setup`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [ADMIN_EARNINGS_LIST_DATA_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const archiveAdminEarningsField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				adminEarningsId: adminEarningsId,
				navigateToLogout: navigateToLogout,
			};
			archiveAdminEarningsById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setAdminEarningsId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		scrollPanelFieldsToTop();
		let path = `/payroll-earning/setup`;
		navigate(path);
	};

	const expandedLabels = ["Earnings", "Payroll Earnings"];
	const expandedAdminEarningsSetupOrSetupItem = "Setup"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Payroll Earnings" &&
				item.label === expandedAdminEarningsSetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: parentLabel === "Payroll Earnings" && item.label === expandedAdminEarningsSetupOrSetupItem && "/payroll-earning/setup",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		adminEarningsName: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const onRowEditClicked = useCallback((params: IAdminEarnings | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/payroll-earning/setup/${params.value}`;
		navigate(path);
	}, []);

	return (
		<Grid
			// sx={{
			// 	pb: "5rem",
			// 	"& .MuiAppBar-root": {
			// 		zIndex: 2,
			// 		top: subMenuVisibility ? "154px" : "112px",
			// 	},
			// }}
			sx={{
				mt: "1rem"
			}}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isArchive}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteAdminEarningsField}
				cancelActionHandler={onCloseHandler}
				testId="adminEarnings-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="Payroll Earnings"
										testId={`adminEarnings`}
										// leftSpacing={1}
										// rightSpacing={1}
										showTitle={false}
										variant="outlined"
									>
										<AdminEarningsInfoSetupFieldsPanel />
									</StandardPanel>
								}
								toolbar={
									<AdminEarningsSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 5, lg: 5, xl: 5 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}
							sx={{ marginTop: "16px" }}
						>

						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"adminEarnings-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
